import { Component, DestroyRef, HostListener, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { toSignal, toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap, catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { PreventionGroupHeaderComponent } from '../../component/prevention-group-header/prevention-group-header.component';
import { PreventionGroupService, PatientDemandDashboardView, TopicDemandRow } from '../../core/services/prevention.service';
import { PreventionCampaignsService } from '../../core/services/prevention-campaigns.service';

type TopicSortKey = 'interestedSessions' | 'exposedSessions' | 'demandRate';

/**
 * "À retenir" (lot "Contrat analytics Campagne", 2026-09-08) -- règle déterministe sur les agrégats
 * déjà exposés, priorité stricte, maximum 2 affichés :
 *   1. campagne à potentiel (intérêt réel, aucune offre actuelle) avec le plus d'intérêts ;
 *   2. campagne AVEC offre qui concentre le plus d'intérêts adressables (addressableInterestCount,
 *      jamais opportunityCount -- circuit questionnaire totalement disjoint, voir rapport d'audit) ;
 *   3. repli uniquement si les 2 premiers types n'ont fourni aucun candidat à eux deux : nombre de
 *      campagnes générant de l'intérêt sans réponse commerciale (superset volontaire de l'ancien KPI
 *      "Opportunités sans offre", qui excluait à tort les campagnes ayant un Need clinique lié mais
 *      aucune offre active -- voir rapport d'audit §02.c).
 */
type DemandInsight =
  | { kind: 'topicToExplore'; topicLabel: string; interestedSessions: number }
  | { kind: 'mostAddressable'; topicLabel: string; addressableInterestCount: number }
  | { kind: 'noCommercialResponse'; count: number };

/**
 * Groupe > Prévention > "Campagne de prévention" (Lot Prevention First UX Phase 1, nom interne
 * Patient Demand -- JAMAIS ce vocabulaire côté UI, voir i18n) -- consomme
 * {@link PreventionGroupService#getDemandDashboard}, AUCUN recalcul de KPI Groupe depuis
 * {@code topics[]} côté Angular : {@code exposedSessionsTotal}/{@code interestedSessionsTotal}/
 * {@code interestRateTotal}/{@code topicsWithoutOfferCount} sont déjà calculés backend (UNION réelle
 * des sessions, jamais une somme des lignes).
 * <p>Hiérarchie de page (§4 du cadrage) : KPI → À retenir (max 2) → Sujets à potentiel / Sujets avec
 * offre (cartes, premier niveau) → tableau détaillé (second niveau, jamais l'élément dominant).
 * <p>Vocabulaire "patients" : les données sont dédupliquées par session/visitId, pas par identité
 * patient -- jamais "X patients intéressés", toujours "sessions".
 */
@Component({
  selector: 'app-prevention-demand',
  standalone: true,
  imports: [TranslatePipe, PreventionGroupHeaderComponent],
  templateUrl: './prevention-demand.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./prevention-demand.component.scss'],
})
export class PreventionDemandComponent {
  private readonly groupSvc = inject(PreventionGroupService);
  private readonly translate = inject(TranslateService);
  private readonly campaignsApi = inject(PreventionCampaignsService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly periodDays = signal(30);
  readonly loadError = signal(false);
  readonly qualityPanelOpen = signal(false);
  /** Topic dont la résolution vers une communication est en cours (bloque un double-clic sur le chevron). */
  readonly resolvingTopicCode = signal<string | null>(null);

  readonly dashboard = toSignal(
    toObservable(this.periodDays).pipe(
      tap(() => this.loadError.set(false)),
      switchMap((days) => this.groupSvc.getDemandDashboard(days).pipe(
        catchError(() => { this.loadError.set(true); return of(null); })
      ))
    ),
    { initialValue: null as PatientDemandDashboardView | null }
  );

  setPeriodDays(days: number): void {
    this.periodDays.set(days);
  }

  /**
   * Chevron de ligne -> ouvre la communication de prévention correspondante. AUCUN lien garanti
   * entre un topic (catalogue global `prevention_topics`, tracking d'intérêt) et une communication
   * (`comm.prevention_campaigns`, sujet texte libre choisi par l'admin) -- résolu ici par
   * correspondance de nom (topicLabel === subject, insensible à la casse/espaces), au clic
   * plutôt que pré-calculé pour toujours refléter les communications actuelles.
   * 1 correspondance -> édition directe. 0 ou plusieurs -> liste "Communications prévention"
   * filtrée sur ce sujet, l'admin tranche lui-même (jamais une correspondance devinée).
   */
  openCampaignForTopic(row: TopicDemandRow): void {
    if (this.resolvingTopicCode()) return;
    this.resolvingTopicCode.set(row.topicCode);
    const label = row.topicLabel.trim().toLowerCase();
    this.campaignsApi.listCampaigns().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (campaigns) => {
        this.resolvingTopicCode.set(null);
        const matches = campaigns.filter(c => c.subject.trim().toLowerCase() === label);
        if (matches.length === 1) {
          this.router.navigate(['/comm-prevention', matches[0].campaignId], {
            queryParams: { returnTo: '/pilotage-prevention/demande' }
          });
        } else {
          this.router.navigate(['/comm-prevention'], { queryParams: { subject: row.topicLabel } });
        }
      },
      error: () => {
        this.resolvingTopicCode.set(null);
        this.router.navigate(['/comm-prevention'], { queryParams: { subject: row.topicLabel } });
      }
    });
  }

  toggleQualityPanel(): void {
    this.qualityPanelOpen.update(v => !v);
  }

  @HostListener('document:click')
  closeQualityPanelOnOutsideClick(): void {
    if (this.qualityPanelOpen()) this.qualityPanelOpen.set(false);
  }

  /**
   * Cohorte GLOBALE Patient Demand sous le seuil small-cohort (correctif small-cohort du lot) --
   * signal UNIQUE {@code exposedSessionsTotal == null}, jamais {@code === 0} : un total non nul mais
   * {@code < smallCohortThreshold} (ex. 5 exposés / 3 intéressés) doit être masqué de la même façon
   * qu'un vrai zéro (bug rapporté, corrigé côté backend ET ici).
   */
  readonly totalsInsufficientData = computed(() => this.dashboard()?.exposedSessionsTotal == null);

  /** Distinction éditoriale uniquement (§7 du cadrage), jamais un calcul différent : aucune donnée
   *  Patient Demand n'a jamais été mesurée pour cette organisation (bandeau "La prévention démarre")
   *  vs. une collecte déjà commencée mais sous le seuil (bandeau "Collecte en cours"). */
  readonly neverTrackedAnyData = computed(() => this.dashboard()?.demandDataAvailableFrom == null);

  /** Cas C (§7) : le total Groupe est visible mais au moins un {@code topics[]} reste
   *  individuellement masqué -- bandeau explicatif au-dessus des sujets/tableau. */
  readonly someTopicsMaskedWhileTotalVisible = computed(() => {
    if (this.totalsInsufficientData()) return false;
    return (this.dashboard()?.topics ?? []).some(t => t.insufficientData);
  });

  /**
   * Cohorte globale des intérêts adressables sous le seuil small-cohort, OU au moins une campagne
   * masquée sur la période (Option C validée) -- signal unique {@code addressablePotentialValueTotal
   * == null}, jamais {@code === 0} (même discipline que {@link totalsInsufficientData}).
   */
  readonly addressablePotentialInsufficientData = computed(() => this.dashboard()?.addressablePotentialValueTotal == null);

  /**
   * Une partie (ou la totalité) de la période sélectionnée précède le début de mesure des intérêts
   * adressables (migration V242) -- jamais interpréter un {@code 0} réel comme fiable dans ce cas,
   * voir {@link formatAddressableCount}/{@link formatAddressableValue}.
   */
  readonly addressableDataPartiallyMissing = computed(() => {
    const d = this.dashboard();
    if (!d) return false;
    if (!d.addressableInterestDataAvailableFrom) return true;
    return new Date(d.periodStart) < new Date(d.addressableInterestDataAvailableFrom);
  });

  // ───────────────────────── À retenir (max 2, priorité stricte) ─────────────────────────

  readonly insights = computed<DemandInsight[]>(() => {
    if (this.totalsInsufficientData()) return [];
    const topics = this.dashboard()?.topics ?? [];
    const result: DemandInsight[] = [];

    const toExplore = topics
        .filter(t => !t.insufficientData && t.denominatorReliable && (t.interestedSessions ?? 0) > 0 && t.offerAvailable === false)
        .reduce<TopicDemandRow | null>((best, t) => (t.interestedSessions! > (best?.interestedSessions ?? 0) ? t : best), null);
    if (toExplore) {
      result.push({ kind: 'topicToExplore', topicLabel: toExplore.topicLabel, interestedSessions: toExplore.interestedSessions! });
    }

    const mostAddressable = topics
        .filter(t => !t.insufficientData && t.offerAvailable === true && (t.addressableInterestCount ?? 0) > 0)
        .reduce<TopicDemandRow | null>((best, t) => (t.addressableInterestCount! > (best?.addressableInterestCount ?? 0) ? t : best), null);
    if (mostAddressable) {
      result.push({ kind: 'mostAddressable', topicLabel: mostAddressable.topicLabel, addressableInterestCount: mostAddressable.addressableInterestCount! });
    }

    // Repli (priorité 3) uniquement si les 2 premiers n'ont pas rempli les 2 emplacements.
    if (result.length < 2) {
      const noCommercialResponseCount = topics
          .filter(t => !t.insufficientData && t.denominatorReliable && (t.interestedSessions ?? 0) > 0 && t.offerAvailable === false)
          .length;
      if (noCommercialResponseCount > 0) {
        result.push({ kind: 'noCommercialResponse', count: noCommercialResponseCount });
      }
    }

    return result.slice(0, 2);
  });

  // ───────────────────────── Tableau détaillé (Lot Simplification/Déduplication, §6/§7 : vue de
  // comparaison PRINCIPALE -- les blocs "Sujets à potentiel"/"Sujets avec offre" ont été supprimés,
  // ils répétaient les mêmes lignes ; l'information "sujet à potentiel" survit comme badge léger
  // directement sur la ligne du sujet, voir template) ─────────────────────────

  readonly topicSort = signal<{ key: TopicSortKey; dir: 'asc' | 'desc' }>({ key: 'interestedSessions', dir: 'desc' });

  /** Lignes masquées (small-cohort) toujours reléguées en fin de liste, jamais assimilées à 0. */
  readonly sortedTopics = computed<TopicDemandRow[]>(() => {
    const rows = this.dashboard()?.topics ?? [];
    const sort = this.topicSort();
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av - bv) * dir;
    });
  });

  toggleTopicSort(key: TopicSortKey): void {
    const current = this.topicSort();
    this.topicSort.set(current.key === key ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  }

  // ───────────────────────── Formatage (jamais 0/0%/reconstruit pour une valeur null) ─────────────────────────

  formatCount(value: number | null | undefined): string {
    if (value == null) return this.translate.instant('interetPatients.states.insufficientData');
    return String(value);
  }

  formatRateTotal(rate: number | null | undefined): string {
    if (rate == null) return this.translate.instant('interetPatients.states.rateUnavailable');
    return Math.round(rate * 100) + '%';
  }

  formatTopicRate(rate: number | null | undefined): string {
    if (rate == null) return this.translate.instant('interetPatients.states.rateUnavailable');
    return Math.round(rate * 100) + '%';
  }

  /**
   * Intérêts adressables/Potentiel identifié (lot "Contrat analytics Campagne", 2026-09-08) --
   * {@code null} reste {@code —} (masqué, comme avant) ; un {@code 0} RÉEL affiché pendant que la
   * période chevauche l'avant-V242 devient un texte explicite plutôt qu'un zéro trompeur (voir
   * {@link addressableDataPartiallyMissing}) -- jamais un historique non mesuré présenté comme un
   * vrai zéro d'intérêt.
   */
  formatAddressableCount(value: number | null | undefined): string {
    if (value == null) return '—';
    if (value === 0 && this.addressableDataPartiallyMissing()) {
      return this.translate.instant('interetPatients.states.notMeasuredPartial');
    }
    return String(value);
  }

  formatAddressableValue(value: number | null | undefined): string {
    if (value == null) return '—';
    if (value === 0 && this.addressableDataPartiallyMissing()) {
      return this.translate.instant('interetPatients.states.notMeasuredPartial');
    }
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  }

  formatDate(iso: string | null | undefined): string {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }
}
